"use client";

import { ConfirmModal } from "@/components/confirm-modal";
import { ACTIONS, type ActionKey } from "@/lib/actions-catalog/catalog";

/**
 * Catalog-aware ConfirmModal.
 *
 *   <ConfirmAction
 *     action="delete"
 *     open={confirmOpen}
 *     onClose={() => setConfirmOpen(false)}
 *     onConfirm={runDelete}
 *     entity="3 issues"
 *   />
 *
 * Pulls the title + confirm-button label + "this can't be undone"
 * boilerplate from the action catalog, so every destructive flow
 * (delete / discard / ignore / disable) reads the same to the
 * operator. Pages override per locale by passing `title` / `body` /
 * `confirmLabel` directly when the catalog default doesn't fit.
 *
 * Use cases that need ad-hoc confirmation copy (e.g. "Move to
 * folder", multi-step wizards) should still reach for the bare
 * `<ConfirmModal>` — the catalog wrapper is for the closed set of
 * canonical danger verbs.
 */
export type ConfirmActionProps = {
  action: ActionKey;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  /** Optional entity name interpolated into the body
   *  ("Delete 3 issues?" instead of the generic copy). */
  entity?: string;
  /** Full title override — wins over the catalog default. */
  title?: string;
  /** Full body override — wins over the catalog default + entity. */
  body?: React.ReactNode;
  /** Confirm-button label override. */
  confirmLabel?: string;
  /** Cancel-button label override. */
  cancelLabel?: string;
};

export function ConfirmAction({
  action,
  open,
  onClose,
  onConfirm,
  entity,
  title,
  body,
  confirmLabel,
  cancelLabel,
}: ConfirmActionProps) {
  const desc = ACTIONS[action];
  if (!desc.destructive || !desc.confirm) {
    // Not a destructive action — calling site bug. The catalog only
    // fills `confirm` on irreversible actions; running ConfirmAction
    // for `approve` / `save` / `open` would silently swallow the
    // operator click.
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[ConfirmAction] "${action}" is not a destructive action — use <ActionButton onClick={…}> directly.`);
    }
    return null;
  }
  const resolvedTitle = title
    ?? (entity ? desc.confirm.title.replace("this item", entity) : desc.confirm.title);
  const resolvedBody = body ?? desc.confirm.bodyHint;
  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      title={resolvedTitle}
      body={resolvedBody}
      confirmLabel={confirmLabel ?? desc.confirm.confirmLabel}
      cancelLabel={cancelLabel}
      destructive
      onConfirm={onConfirm}
    />
  );
}
